import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Mail, Lock, AlertTriangle, CheckCircle, KeyRound, RefreshCw } from 'lucide-react';
import { authService } from '../../services/authService';
import { ResetPasswordData } from '../../types/auth';
import { useLanguage } from '../../hooks/useLanguage';

export const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [success, setSuccess] = useState(false);

  // Refs pour validation personnalisée
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Configuration de la validation personnalisée
  const setupCustomValidation = () => {
    const setupInput = (
        ref: React.RefObject<HTMLInputElement>,
        requiredMessage: string,
        typeMessage?: string
    ) => {
      const input = ref.current;
      if (!input) return;

      const handleInvalid = () => {
        if (input.validity.valueMissing) {
          input.setCustomValidity(requiredMessage);
        } else if (input.validity.typeMismatch && typeMessage) {
          input.setCustomValidity(typeMessage);
        }
      };

      const handleInput = () => {
        input.setCustomValidity('');
      };

      input.addEventListener('invalid', handleInvalid);
      input.addEventListener('input', handleInput);
    };

    setupInput(emailRef, t('resetPassword.validation.emailRequired'), t('resetPassword.validation.emailInvalid'));
    setupInput(codeRef, t('resetPassword.validation.codeRequired'));
    setupInput(newPasswordRef, t('resetPassword.validation.newPasswordRequired'));
    setupInput(confirmPasswordRef, t('resetPassword.validation.confirmPasswordRequired'));
  };

  useEffect(() => {
    setupCustomValidation();
  }, [t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    e.target.setCustomValidity('');
    if (name === 'email') {
      setCodeResent(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.code) {
      setError(t('resetPassword.validation.emailAndCodeRequired'));
      return;
    }

    try {
      setVerifyingCode(true);
      await authService.verifyResetCode(formData.email, formData.code);
      setCodeVerified(true);
    } catch (err: any) {
      setError(t('resetPassword.validation.invalidOrExpiredCode'));
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email) {
      setError(t('resetPassword.validation.emailRequired'));
      return;
    }

    try {
      setResendingCode(true);
      setError('');
      await authService.requestPasswordReset(formData.email);
      setCodeResent(true);
      setFormData(prev => ({
        ...prev,
        code: ''
      }));
    } catch (err: any) {
      setError(err.message || t('resetPassword.validation.codeResendError'));
    } finally {
      setResendingCode(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmNewPassword) {
      setError(t('resetPassword.validation.passwordsMismatch'));
      return;
    }

    // Vérifier la complexité du mot de passe
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.newPassword)) {
      setError(t('resetPassword.validation.passwordComplexity'));
      return;
    }

    try {
      setLoading(true);
      const resetData: ResetPasswordData & { code: string } = {
        email: formData.email,
        newPassword: formData.newPassword,
        code: formData.code
      };
      await authService.resetPassword(resetData);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.message || t('resetPassword.validation.resetError'));
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : ''}`}>
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-xl">
          <div className="flex flex-col items-center">
            <Shield className="h-12 w-12 text-blue-600" />
            <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
              {t('resetPassword.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {!codeVerified
                  ? t('resetPassword.subtitle.step1')
                  : t('resetPassword.subtitle.step2')}
            </p>
          </div>

          {!codeVerified ? (
              // Étape 1: Vérification du code
              <form className="mt-8 space-y-6" onSubmit={handleVerifyCode} noValidate>
                {error && (
                    <div className="rounded-lg bg-red-50 p-4 text-sm flex items-center border border-red-200">
                      <AlertTriangle className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <span className="text-red-700">{error}</span>
                    </div>
                )}

                {codeResent && (
                    <div className="rounded-lg bg-green-50 p-4 text-sm flex items-center border border-green-200">
                      <CheckCircle className={`h-5 w-5 text-green-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <span className="text-green-700">
                  {t('resetPassword.success.newCodeSent', { email: formData.email })}
                </span>
                    </div>
                )}

                <div className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('resetPassword.form.email')}
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                          ref={emailRef}
                          id="email"
                          name="email"
                          type="email"
                          required
                          className={`block w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                          placeholder={t('resetPassword.form.emailPlaceholder')}
                          value={formData.email}
                          onChange={handleChange}
                          dir="ltr"
                      />
                    </div>
                  </div>

                  {/* Code Input */}
                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('resetPassword.form.code')}
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                        <KeyRound className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                          ref={codeRef}
                          id="code"
                          name="code"
                          type="text"
                          required
                          maxLength={4}
                          pattern="[0-9]{4}"
                          className={`block w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                          placeholder={t('resetPassword.form.codePlaceholder')}
                          value={formData.code}
                          onChange={handleChange}
                          dir="ltr"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-4">
                  <button
                      type="submit"
                      disabled={verifyingCode}
                      className={`flex justify-center items-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                          verifyingCode ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {verifyingCode ? (
                        <>
                          <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('resetPassword.form.verifying')}
                        </>
                    ) : (
                        t('resetPassword.form.verifyCode')
                    )}
                  </button>

                  <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendingCode || !formData.email}
                      className={`flex justify-center items-center py-2 px-4 border border-blue-300 rounded-lg text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                          (resendingCode || !formData.email) ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {resendingCode ? (
                        <>
                          <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-blue-600`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('resetPassword.form.sending')}
                        </>
                    ) : (
                        <>
                          <RefreshCw className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {t('resetPassword.form.getNewCode')}
                        </>
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                        to="/login"
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-150"
                    >
                      {t('resetPassword.actions.backToLogin')}
                    </Link>
                  </div>
                </div>
              </form>
          ) : (
              // Étape 2: Saisie du nouveau mot de passe
              <form className="mt-8 space-y-6" onSubmit={handleResetPassword} noValidate>
                {error && (
                    <div className="rounded-lg bg-red-50 p-4 text-sm flex items-center border border-red-200">
                      <AlertTriangle className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <span className="text-red-700">{error}</span>
                    </div>
                )}

                {success && (
                    <div className="rounded-lg bg-green-50 p-4 text-sm flex items-center border border-green-200">
                      <CheckCircle className={`h-5 w-5 text-green-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <span className="text-green-700">
                  {t('resetPassword.success.passwordReset')}
                </span>
                    </div>
                )}

                <div className="space-y-4">
                  {/* New Password Input */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('resetPassword.form.newPassword')}
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                          ref={newPasswordRef}
                          id="newPassword"
                          name="newPassword"
                          type="password"
                          required
                          className={`block w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                          placeholder={t('resetPassword.form.newPasswordPlaceholder')}
                          value={formData.newPassword}
                          onChange={handleChange}
                          dir={isRTL ? 'rtl' : 'ltr'}
                      />
                    </div>
                  </div>

                  {/* Confirm Password Input */}
                  <div>
                    <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('resetPassword.form.confirmPassword')}
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                          ref={confirmPasswordRef}
                          id="confirmNewPassword"
                          name="confirmNewPassword"
                          type="password"
                          required
                          className={`block w-full ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                          placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
                          value={formData.confirmNewPassword}
                          onChange={handleChange}
                          dir={isRTL ? 'rtl' : 'ltr'}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-4">
                  <button
                      type="submit"
                      disabled={loading || success}
                      className={`flex justify-center items-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                          (loading || success) ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {loading ? (
                        <>
                          <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('resetPassword.form.resetting')}
                        </>
                    ) : (
                        t('resetPassword.form.resetPassword')
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                        to="/login"
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-150"
                    >
                      {t('resetPassword.actions.backToLogin')}
                    </Link>
                  </div>
                </div>
              </form>
          )}

          {/* Password Requirements */}
          {codeVerified && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  {t('resetPassword.requirements.title')}
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center">
                    <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                    {t('resetPassword.requirements.minLength')}
                  </li>
                  <li className="flex items-center">
                    <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                    {t('resetPassword.requirements.uppercase')}
                  </li>
                  <li className="flex items-center">
                    <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                    {t('resetPassword.requirements.digit')}
                  </li>
                  <li className="flex items-center">
                    <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                    {t('resetPassword.requirements.specialChar')}
                  </li>
                </ul>
              </div>
          )}
        </div>
      </div>
  );
};